import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  CornerDownLeft,
  DoorOpen,
  LocateFixed,
  Map as MapIcon,
  Navigation,
  Search,
  Settings2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { createGame } from './engine';
import { resolveAvatar } from './avatar';
import TownSettingsPanel from './TownSettingsPanel';
import {
  DEFAULT_TOWN_PREFERENCES,
  loadTownPreferences,
  saveTownPreferences,
} from './townPreferences';
import './pixelTown.css';

let toastSeq = 0;

/**
 * 像素舞台：托管引擎实例 + 全部覆盖层（面包屑 / 按键提示 / 音效开关 / 交互对话框 / toast）
 * props:
 *   scene          场景描述（engine.mount 的输入），为 null 时不挂载
 *   avatar         形象（id 形式，内部 resolve 后传给引擎）
 *   paused         暂停移动与交互（弹窗打开时）
 *   welcomeToasts  场景首次挂载后依次弹出的提示
 *   onAction       地垫动作回调 (action, mat)
 *   onEscape       Esc 回调
 *   onCustomizeAvatar 打开形象编辑器
 */
export default function PixelStage({
  scene,
  avatar,
  paused = false,
  welcomeToasts = [],
  onAction,
  onEscape,
  onCustomizeAvatar,
}) {
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const mountedRef = useRef(true);
  const actionRef = useRef(onAction);
  actionRef.current = onAction;
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;

  const [crumb, setCrumb] = useState('');
  const [prompt, setPrompt] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [showNavigator, setShowNavigator] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [navigationQuery, setNavigationQuery] = useState('');
  const [preferences, setPreferences] = useState(loadTownPreferences);
  const [soundOn, setSoundOn] = useState(() => {
    try {
      return localStorage.getItem('pixel-town:sound') !== 'off';
    } catch {
      return true;
    }
  });

  const pushToast = useCallback((text, ms = 3200) => {
    const id = ++toastSeq;
    setToasts((list) => [...list.slice(-3), { id, text }]);
    setTimeout(() => {
      if (mountedRef.current) setToasts((list) => list.filter((t) => t.id !== id));
    }, ms);
  }, []);

  // 引擎生命周期：与组件同生共死
  useEffect(() => {
    mountedRef.current = true;
    const game = createGame(canvasRef.current, {
      onPrompt: setPrompt,
      onCrumb: setCrumb,
      onToast: pushToast,
      onAction: (action, mat) => actionRef.current?.(action, mat),
      onEscape: () => escapeRef.current?.(),
    });
    gameRef.current = game;
    return () => {
      mountedRef.current = false;
      game.destroy();
      gameRef.current = null;
    };
  }, [pushToast]);

  // 偏好先于场景挂载，避免禁用位置记忆时读取到旧位置。
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    game.setTownSize(preferences.townSize);
    game.setMovementSpeed(preferences.movementSpeed);
    game.setAmbience(preferences.ambience);
    game.setRememberPosition(preferences.rememberPosition);
    game.setNameTags(preferences.showNameTags);
    saveTownPreferences(preferences);
  }, [preferences]);

  // 场景 / 形象 / 暂停 同步进引擎
  useEffect(() => {
    if (scene && gameRef.current) gameRef.current.mount(scene);
  }, [scene]);

  const resolvedAvatar = useMemo(() => (avatar ? resolveAvatar(avatar) : null), [avatar]);
  useEffect(() => {
    if (resolvedAvatar && gameRef.current) gameRef.current.setAvatar(resolvedAvatar);
  }, [resolvedAvatar]);

  useEffect(() => {
    gameRef.current?.setPaused(paused || showNavigator || showSettings);
  }, [paused, showNavigator, showSettings]);

  useEffect(() => {
    setShowNavigator(false);
    setShowSettings(false);
    setNavigationQuery('');
  }, [scene?.id]);

  useEffect(() => {
    if (!showNavigator && !showSettings) return undefined;
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setShowNavigator(false);
      setShowSettings(false);
      canvasRef.current?.focus({ preventScroll: true });
    };
    window.addEventListener('keydown', closeOnEscape, true);
    return () => window.removeEventListener('keydown', closeOnEscape, true);
  }, [showNavigator, showSettings]);

  useEffect(() => {
    gameRef.current?.setSound(soundOn);
    try {
      localStorage.setItem('pixel-town:sound', soundOn ? 'on' : 'off');
    } catch { /* 本地存储不可用时仍可正常游览 */ }
  }, [soundOn]);

  // 欢迎 toast：每个场景只播一次
  const welcomedSceneRef = useRef(null);
  useEffect(() => {
    if (!scene || welcomedSceneRef.current === scene || welcomeToasts.length === 0) return undefined;
    welcomedSceneRef.current = scene;
    const timers = welcomeToasts.map((text, i) => (
      setTimeout(() => { if (mountedRef.current) pushToast(text, 3600); }, 500 + i * 1800)
    ));
    return () => timers.forEach(clearTimeout);
  }, [scene, welcomeToasts, pushToast]);

  const toggleSound = () => {
    setSoundOn((prev) => !prev);
  };

  const updatePreference = useCallback((key, value) => {
    setPreferences((current) => ({ ...current, [key]: value }));
  }, []);

  const resetPreferences = useCallback(() => {
    setPreferences({ ...DEFAULT_TOWN_PREFERENCES });
    pushToast('小镇设置已恢复默认', 2200);
  }, [pushToast]);

  const openAvatarCustomizer = () => {
    setShowSettings(false);
    onCustomizeAvatar?.();
  };

  const navigationItems = useMemo(() => {
    const seen = new Set();
    return (scene?.mats || []).reduce((items, mat, index) => {
      const action = mat?.action || {};
      const identity = action.type === 'route'
        ? `route:${action.path || mat.label || index}`
        : `${action.type || 'mat'}:${action.id || action.path || mat.label || index}`;
      if (seen.has(identity)) return items;
      seen.add(identity);
      items.push({
        key: identity,
        label: String(mat.fullLabel || mat.label || '未命名入口').replace(/^进入\s+/, ''),
        description: mat.description || '',
        projectId: mat.projectId ?? '',
        coverUrl: mat.coverUrl || '',
        action,
        mat,
        tx: mat.tx,
        ty: mat.ty,
      });
      return items;
    }, []);
  }, [scene]);

  const visibleNavigationItems = useMemo(() => {
    const query = navigationQuery.trim().toLocaleLowerCase();
    if (!query) return navigationItems;
    return navigationItems.filter((item) => (
      `${item.label} ${item.description} ${item.projectId}`.toLocaleLowerCase().includes(query)
    ));
  }, [navigationItems, navigationQuery]);

  const enterDestination = (item) => {
    if (!item.action?.type || !actionRef.current) return;
    setShowNavigator(false);
    actionRef.current(item.action, item.mat);
  };

  const walkToDestination = (item) => {
    const started = gameRef.current?.walkToTile(item.tx, item.ty);
    setShowNavigator(false);
    pushToast(started ? `正在前往：${item.label}` : `暂时无法到达：${item.label}`, 2400);
    requestAnimationFrame(() => canvasRef.current?.focus({ preventScroll: true }));
  };

  const returnToEntrance = () => {
    const reset = gameRef.current?.resetPosition();
    setShowNavigator(false);
    if (reset) pushToast('已回到当前地图入口', 2200);
    requestAnimationFrame(() => canvasRef.current?.focus({ preventScroll: true }));
  };

  return (
    <div className="pixel-town-stage" data-view-mode="pixel">
      <div className="pixel-town-stage__viewport">
        <canvas
          ref={canvasRef}
          className="pixel-town-stage__canvas"
          tabIndex={0}
          role="application"
          aria-label="漫剧小镇地图。使用方向键或 WASD 移动，回车交互，也可以点击地图移动。"
        >
          当前浏览器不支持小镇地图画布。
        </canvas>

        {crumb && (
          <div className="pixel-town-overlay pixel-town-crumb">
            <span className="pixel-town-crumb__mode">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" shapeRendering="crispEdges" aria-hidden="true">
                <path d="M2 0h2v2H2zM8 0h2v2H8zM0 2h12v2H0zM0 4h2v6H0zM10 4h2v6h-2zM2 6h2v2H2zM8 6h2v2H8zM2 10h8v2H2z" />
              </svg>
              像素片场
            </span>
            <span className="pixel-town-crumb__location">{crumb}</span>
          </div>
        )}

        <div className="pixel-town-stage-controls" data-pixel-town-ignore-keys="true">
          <button
            type="button"
            onClick={() => {
              setShowNavigator(false);
              setShowSettings((open) => !open);
            }}
            className="pixel-town-icon-button pixel-town-settings-toggle"
            title={showSettings ? '关闭小镇设置' : '小镇设置'}
            aria-label={showSettings ? '关闭小镇设置' : '打开小镇设置'}
            aria-expanded={showSettings}
            aria-controls="pixel-town-settings"
          >
            <Settings2 size={16} />
          </button>

          <button
            type="button"
            onClick={() => {
              setShowSettings(false);
              setShowNavigator((open) => !open);
            }}
            className="pixel-town-icon-button pixel-town-navigator-toggle"
            title={showNavigator ? '关闭快速导航' : '快速导航'}
            aria-label={showNavigator ? '关闭快速导航' : '打开快速导航'}
            aria-expanded={showNavigator}
            aria-controls="pixel-town-navigator"
          >
            <MapIcon size={16} />
          </button>

          <button
            type="button"
            onClick={toggleSound}
            className="pixel-town-icon-button pixel-town-sound"
            title={soundOn ? '关闭音效' : '开启音效'}
            aria-label={soundOn ? '关闭音效' : '开启音效'}
            aria-pressed={soundOn}
          >
            {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
        </div>

        {showNavigator && (
          <section
            id="pixel-town-navigator"
            className="pixel-town-navigator"
            role="dialog"
            aria-modal="false"
            aria-label="当前地图快速导航"
            data-pixel-town-ignore-keys="true"
          >
            <header className="pixel-town-navigator__header">
              <div>
                <strong>快速导航</strong>
                <span>{visibleNavigationItems.length} / {navigationItems.length} 个入口</span>
              </div>
              <button
                type="button"
                className="pixel-town-navigator__close"
                onClick={() => setShowNavigator(false)}
                aria-label="关闭快速导航"
              >
                <X size={15} />
              </button>
            </header>

            <p className="pixel-town-navigator__hint">直接进入工作区，或沿着小镇走过去。</p>
            <label className="pixel-town-navigator__search">
              <Search size={14} />
              <input
                value={navigationQuery}
                onChange={(event) => setNavigationQuery(event.target.value)}
                placeholder="搜索名称或项目 ID"
                aria-label="搜索入口名称或项目 ID"
                autoFocus
              />
            </label>

            <div className="pixel-town-navigator__list">
              {visibleNavigationItems.map((item) => (
                <div key={item.key} className="pixel-town-navigator__entry">
                  <div className="pixel-town-navigator__entry-heading">
                    <span className="pixel-town-navigator__entry-mark" aria-hidden="true">
                      {item.coverUrl ? <img src={item.coverUrl} alt="" loading="lazy" /> : <DoorOpen size={17} />}
                    </span>
                    <div className="pixel-town-navigator__entry-copy">
                      <strong title={item.label}>{item.label}</strong>
                      <span>{item.description || (item.projectId ? `项目 ID ${item.projectId}` : '当前地图入口')}</span>
                    </div>
                  </div>
                  <div className="pixel-town-navigator__entry-actions">
                    <button
                      type="button"
                      className="pixel-town-navigator__enter"
                      onClick={() => enterDestination(item)}
                      disabled={!item.action?.type || !onAction}
                      aria-label={`直接进入 ${item.label}`}
                    >
                      直接进入 <ArrowUpRight size={13} />
                    </button>
                    <button
                      type="button"
                      className="pixel-town-navigator__walk"
                      onClick={() => walkToDestination(item)}
                      aria-label={`走过去 ${item.label}`}
                    >
                      <Navigation size={12} /> 走过去
                    </button>
                  </div>
                </div>
              ))}
              {visibleNavigationItems.length === 0 && (
                <p className="pixel-town-navigator__empty">没有匹配的入口</p>
              )}
            </div>

            <button
              type="button"
              className="pixel-town-navigator__reset"
              onClick={returnToEntrance}
            >
              <LocateFixed size={14} />
              回到地图入口
            </button>
          </section>
        )}

        {showSettings && (
          <TownSettingsPanel
            preferences={preferences}
            onChange={updatePreference}
            onReset={resetPreferences}
            onCustomizeAvatar={onCustomizeAvatar ? openAvatarCustomizer : undefined}
            onClose={() => setShowSettings(false)}
          />
        )}

        {prompt && (
          <button
            type="button"
            onClick={() => gameRef.current?.interact()}
            className="pixel-town-prompt"
            aria-label={prompt}
          >
            <CornerDownLeft size={16} />
            <span>{prompt}</span>
          </button>
        )}

        <div className="pixel-town-toasts" aria-live="polite" aria-atomic="false">
          {toasts.map((t) => (
            <div key={t.id} className="pixel-town-toast">{t.text}</div>
          ))}
        </div>
      </div>
    </div>
  );
}
